/**
 * LOCAL DEV ONLY — mock of `@auth0/auth0-react`.
 *
 * When `DEV_AUTH_MOCK=true` (server-only build var) and the build is not a
 * production build, `next.config.js` aliases the `@auth0/auth0-react` package to
 * this module, so every consumer of `useAuth0` / `withAuthenticationRequired` /
 * `Auth0Provider` behaves as a logged-in dev user without real Auth0 credentials.
 *
 * Persona switch: the mock can impersonate `admin` (default), `doctor`, or
 * `worker`. The selected persona is stored in localStorage and drives both the
 * UI (the token's `permissions` claim is decoded by PermissionGate/hasScope) and
 * the API (which decodes the same token; admin keeps the grant-all bypass while
 * doctor/worker flow through the REAL row-scoping — see api/mblab/dev_auth.py).
 * Both ends are inert by default. NEVER enable in staging/production.
 */
import React from "react";

export type DevTier = "admin" | "doctor" | "worker" | "recepcionista";

// True only when the active token is a dev-mock persona (sub "dev|..."). Used to
// gate dev-only UI (the persona switcher, the Settings entry) — under real Auth0
// this is always false, so nothing dev-only ships.
export const isDevMockSub = (sub?: string | null): boolean =>
  !!sub?.startsWith("dev|");

const STORAGE_KEY = "dev-mock-tier";

// Persona scope sets MIRROR the nonprod Auth0 roles. Admin is the full app
// scope set (incl. the read:order-all admin marker); doctor/worker match
// "CRM Doctor/Worker role - nonprod" exactly.
const ADMIN_PERMS = [
  "read:doctor", "write:doctor", "delete:doctor",
  "read:order", "write:order", "delete:order",
  "read:order-status", "write:order-status", "delete:order-status",
  "read:order-history", "read:order-message", "write:order-message",
  "read:payment", "write:payment", "delete:payment",
  "read:employee", "write:employee", "delete:employee",
  "read:supplier", "write:supplier", "delete:supplier",
  "read:material", "write:material", "delete:material",
  "read:equipment", "write:equipment", "delete:equipment",
  "read:buy-order", "write:buy-order", "delete:buy-order",
  "read:usage-log", "write:usage-log", "delete:usage-log",
  "read:cash-cut", "read:printer", "write:printer", "read:order-all",
  "read:dashboard",
];
const DOCTOR_PERMS = [
  "read:doctor", "read:order", "read:order-message",
  "read:payment", "write:order-draft", "write:order-message",
];
const WORKER_PERMS = [
  "read:order", "read:order-message", "read:order-status",
  "write:order", "write:order-message",
];
// Mirrors "CRM Recepcionista role - nonprod" (29 perms as of 2026-07-13):
// front desk + office-manager expansion. Notably NO read:order-all.
const RECEPCIONISTA_PERMS = [
  "read:reception", "read:order-cost-request", "write:order-cost-request",
  "read:order", "write:order", "read:order-status",
  "read:order-message", "write:order-message",
  "read:payment", "write:payment",
  "read:doctor", "write:doctor",
  "read:employee", "write:employee",
  "read:printer", "write:printer",
  "read:supplier", "write:supplier", "delete:supplier",
  "read:material", "write:material", "delete:material",
  "read:equipment", "write:equipment", "delete:equipment",
  "read:buy-order", "write:buy-order", "delete:buy-order",
  "read:usage-log", "write:usage-log", "delete:usage-log",
];

export const DEV_PERSONAS: Record<
  DevTier,
  { sub: string; name: string; permissions: string[] }
> = {
  admin: { sub: "dev|mock", name: "Dev Admin (mock)", permissions: ADMIN_PERMS },
  doctor: { sub: "dev|doctor", name: "Dev Doctor (mock)", permissions: DOCTOR_PERMS },
  worker: { sub: "dev|worker", name: "Dev Worker (mock)", permissions: WORKER_PERMS },
  recepcionista: {
    sub: "dev|recepcionista",
    name: "Dev Recepcionista (mock)",
    permissions: RECEPCIONISTA_PERMS,
  },
};

const b64 = (obj: unknown): string =>
  // btoa exists in browsers and Node >= 16 (Next dev SSR + vitest).
  btoa(JSON.stringify(obj));

/** Build a dev-mock-shaped JWT (alg "none", unverifiable ".devmock" signature)
 *  for a persona. The API recognises it by the suffix; the FE decodes its
 *  `permissions` claim for UI gating. */
export const buildDevToken = (tier: DevTier): string => {
  const p = DEV_PERSONAS[tier];
  const header = b64({ alg: "none", typ: "JWT" });
  const payload = b64({
    sub: p.sub,
    name: p.name,
    email: "dev@localhost",
    scope: "openid profile email",
    permissions: p.permissions,
    email_verified: true,
  });
  return `${header}.${payload}.devmock`;
};

export const getDevTier = (): DevTier => {
  if (typeof window === "undefined") return "admin";
  const t = window.localStorage.getItem(STORAGE_KEY);
  return t && t in DEV_PERSONAS ? (t as DevTier) : "admin";
};

export const setDevTier = (tier: DevTier): void => {
  if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, tier);
};

// Backward-compat export (admin token). The API matches by suffix, not this
// exact literal, so personas work regardless.
export const DEV_MOCK_TOKEN = buildDevToken("admin");

const mockUser = () => {
  const p = DEV_PERSONAS[getDevTier()];
  return {
    name: p.name,
    email: "dev@localhost",
    sub: p.sub,
    nickname: "dev",
    email_verified: true,
    picture: "",
    updated_at: "1970-01-01T00:00:00.000Z",
  };
};

export function Auth0Provider({ children }: { children?: React.ReactNode }) {
  return <>{children}</>;
}

export function useAuth0() {
  const token = buildDevToken(getDevTier());
  return {
    isAuthenticated: true,
    isLoading: false,
    error: undefined,
    user: mockUser(),
    getAccessTokenSilently: async (_opts?: unknown) => token,
    getAccessTokenWithPopup: async (_opts?: unknown) => token,
    getIdTokenClaims: async () => ({ __raw: token }),
    loginWithRedirect: async (_opts?: unknown) => {},
    loginWithPopup: async (_opts?: unknown) => {},
    logout: (_opts?: unknown) => {},
    handleRedirectCallback: async () => ({ appState: undefined }),
  };
}

export function withAuthenticationRequired<P>(Component: React.ComponentType<P>) {
  return Component;
}
